import {Badge, Box, Flex, Text, Tooltip, VStack} from '@chakra-ui/react';
import {InfoOutlineIcon} from '@chakra-ui/icons';
import {useCommand} from '@synako/haligator';
import {Navigable} from '@synako/halitrek';
import {SyntheseEGalimDesc} from 'api-types/ffconnect-hal';
import {useTranslation} from 'react-i18next';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {CodeLabelEGalim} from 'api-types/ffconnect';
import {LABELS_ELIGIBLES, LABELS_OFFICIELS} from '../egalim-constantes';

/**
 * Bloc labels du dashboard ÉGalim.
 * Affiche les labels officiels (badges statiques) et les labels éligibles.
 */
export const EgalimLabels: React.FC<{
  syntheseNav: Navigable<SyntheseEGalimDesc>;
  labelsSelectionnes: CodeLabelEGalim[];
  onLabelsEligiblesChange: (labels: CodeLabelEGalim[]) => void;
}> = ({syntheseNav, labelsSelectionnes, onLabelsEligiblesChange}) => {
  const {t} = useTranslation();
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);

  // Commande pour modifier les labels éligibles non officiels sélectionnés par le client
  const {submit, isSubmitting} = useCommand(
    syntheseNav,
    'c:modifier-labels-eligibles',
    {
      onSuccess: () => {
        // Le state sera rafraîchi automatiquement via HAL
      },
      onError,
    },
  );

  // Gestion du clic sur un label éligible non officiel
  const handleToggleLabel = (code: CodeLabelEGalim) => {
    if (isSubmitting) return;
    const isSelected = labelsSelectionnes.includes(code);
    const newLabels = isSelected
      ? labelsSelectionnes.filter((l) => l !== code)
      : [...labelsSelectionnes, code];
    onLabelsEligiblesChange(newLabels);
    submit({labels_eligibles: newLabels});
  };

  return (
    <VStack
      align='stretch'
      spacing={4}
      bg='gray.75'
      borderRadius='8px'
      p='20px'
    >
      {/* Labels officiels */}
      <Box>
        <Text
          fontWeight='600'
          fontSize='sm'
          color='gray.dark'
          mb={2}
        >
          {t('egalim.labels_officiels')}
        </Text>
        <Flex
          gap={2}
          wrap='wrap'
        >
          {LABELS_OFFICIELS.map(({code, libelle}) => (
            <Badge
              key={code}
              px={3}
              py={1}
              borderRadius='full'
              bg='blue.sky_light'
              color='blue.main'
              fontWeight='500'
              fontSize='xs'
              textTransform='none'
            >
              {libelle}
            </Badge>
          ))}
        </Flex>
      </Box>

      {/* Labels éligibles non officiels */}
      <Box>
        <Flex
          align='center'
          gap={1}
          mb={2}
        >
          <Text
            fontWeight='600'
            fontSize='sm'
            color='gray.dark'
          >
            {t('egalim.labels_eligibles')}
          </Text>
          <Tooltip
            hasArrow
            label={t('egalim.labels_eligibles_info')}
            placement='top'
          >
            <InfoOutlineIcon
              color='gray.dark'
              boxSize='14px'
            />
          </Tooltip>
        </Flex>
        <Flex
          gap={2}
          wrap='wrap'
        >
          {LABELS_ELIGIBLES.map(({code, libelle}) => {
            const isSelected = labelsSelectionnes.includes(code);
            return (
              <Badge
                key={code}
                px={3}
                py={1}
                border='1px solid'
                borderColor={isSelected ? 'blue.sky_light' : 'gray.dark'}
                borderRadius='full'
                bg={isSelected ? 'blue.sky_light' : 'gray.75'}
                color={isSelected ? 'blue.main' : 'gray.dark'}
                fontWeight='500'
                fontSize='xs'
                textTransform='none'
                cursor={isSubmitting ? 'wait' : 'pointer'}
                opacity={isSubmitting ? 0.6 : 1}
                onClick={() => {
                  handleToggleLabel(code);
                }}
              >
                {libelle}
              </Badge>
            );
          })}
        </Flex>
      </Box>
    </VStack>
  );
};
